import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { OktaAuth } from '@okta/okta-auth-js';
import { EmailRegex } from '../../../utils/validations/formRegexes';
import { i18translation } from '../../../locale/translations/i18NsPaths';
import { closeOktaSession } from '../../../utils/oktaInstance';

export const getLoginFormSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .matches(EmailRegex, t(i18translation.LoginScreen.validations.emailRegex))
      .required(t(i18translation.LoginScreen.validations.emailRequired))
  });

/**
 * Determines if we should force Okta to prompt for login credentials.
 * When enableKeepOktaSession is ON, checks if there's an active session with the same email.
 * Returns true if login should be forced, false if auto-login is allowed.
 */
export const shouldForceOktaLogin = async (
  oktaAuth: OktaAuth,
  email: string,
  enableKeepOktaSession: boolean
): Promise<boolean> => {
  // If flag is OFF, don't force login (original behavior)
  if (!enableKeepOktaSession) {
    return false;
  }

  // Flag is ON - default to forcing login for safety
  try {
    // session.get() checks the actual Okta server session (works even when local tokens are cleared)
    const session = (await oktaAuth.session.get()) as
      | { status: string; login?: string }
      | undefined;

    // Only allow auto-login if session is ACTIVE with the SAME email
    // INACTIVE session or different email = force login (IDP might have different user's session)
    if (
      session?.status === 'ACTIVE' &&
      session?.login &&
      session.login.toLowerCase() === email.toLowerCase()
    ) {
      return false; // Same user, allow auto-login
    }
  } catch {
    // session.get() may fail due to third-party cookie blocking
    // Fall back to checking local tokens
    try {
      const tokens = await oktaAuth.tokenManager.getTokens();

      if (Object.keys(tokens).length > 0) {
        const currentUser = await oktaAuth.getUser();

        // Only allow auto-login if local tokens show same email
        if (currentUser?.email && currentUser.email.toLowerCase() === email.toLowerCase()) {
          return false; // Same user, allow auto-login
        }
      }
    } catch {
      // If we can't verify session, force login for safety
    }
  }

  return true; // Force login by default when flag is ON
};

interface HandleSocialLoginOptions {
  idp: string;
  oktaAuth: OktaAuth;
  loginType?: string;
  enableKeepOktaSession?: boolean;
}

export const handleSocialLogin = async ({
  idp,
  oktaAuth,
  loginType,
  enableKeepOktaSession
}: HandleSocialLoginOptions) => {
  loginType && setLoginType(loginType);

  // Close Okta session before social login when flag is enabled
  // This ensures the user authenticates fresh with the social provider
  if (enableKeepOktaSession) {
    await closeOktaSession();
  }

  await oktaAuth.signInWithRedirect({ idp });
};

export const setLoginType = (loginType: string) => {
  window.sessionStorage.setItem('loginType', loginType);
};

export const emailValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .matches(EmailRegex, t(i18translation.LoginScreen.forgotPassScreen.validations.emailRegex))
      .required(t(i18translation.LoginScreen.forgotPassScreen.validations.emailRequired))
  });
